package com.taocent.simple.compose.component.blockrichtext.internal.block

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import com.taocent.simple.compose.component.blockrichtext.*
import com.taocent.simple.compose.component.blockrichtext.BlockState
import com.taocent.simple.compose.component.blockrichtext.ExperimentalBlockRichTextApi
import com.taocent.simple.compose.component.blockrichtext.RichTextTextField
import com.taocent.simple.compose.component.blockrichtext.TableBlock
import com.taocent.simple.compose.component.blockrichtext.TextBlock
import com.taocent.simple.compose.component.blockrichtext.internal.platform.InterceptDeleteBackwardTextInput
import com.taocent.simple.compose.component.richtext.core.internal.platform.InterceptImePasteTextInput

@Composable
@OptIn(ExperimentalBlockRichTextApi::class)
internal fun TextBlockItem(
    block: TextBlock,
    blockState: BlockState,
    focusRequester: FocusRequester,
    modifier: Modifier = Modifier
) {
    val blockRichTextState = blockState.getBlockState(block.id)

    InterceptDeleteBackwardTextInput(
        onDeleteBackward = {
            val value = blockRichTextState.textFieldValue
            if (value.text.isEmpty() && value.selection.collapsed && value.selection.min == 0) {
                blockState.tryDeleteAtBlockStart(block.id)
            } else {
                false
            }
        }
    ) {
        // Android 软键盘/EditMenu 的"粘贴"按钮走 InputConnection.commitText,
        // 不经过 onCopy/onPaste,所以必须在 IME EditCommand 层拦截(由 core 的
        // InterceptImePasteTextInput 统一提供):
        // - Block JSON:整体替换 blocks(0 级 onPasteJson hook)
        // - emoji shortcode:还原 emoji 渲染(1 级 core 处理)
        // 其他平台 no-op,不影响 iOS / Desktop / js / wasmJs。
        InterceptImePasteTextInput(
            richTextState = blockRichTextState,
            onPasteJson = { json ->
                blockState.isBlockJson(json) && blockState.pasteJson(json)
            },
        ) {
            RichTextTextField(
                state = blockRichTextState,
        modifier = modifier
            .fillMaxWidth()
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        if (event.type == androidx.compose.ui.input.pointer.PointerEventType.Press) {
                            blockState.navClearSelection()
                        }
                    }
                }
            }
            .onPreviewKeyEvent { event ->
                if (event.type == KeyEventType.KeyDown) {
                    val selection = blockRichTextState.textFieldValue.selection
                    val textLen = blockRichTextState.textFieldValue.text.length
                    if (event.key == Key.Backspace) {
                        if (selection.collapsed && selection.min == 0) {
                            return@onPreviewKeyEvent blockState.tryDeleteAtBlockStart(block.id)
                        }
                    }
                    if (event.key == Key.DirectionUp) {
                        if (selection.collapsed && selection.min == 0) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index > 0 && blockState.blocks[index - 1] is TableBlock) {
                                val prevTable = blockState.blocks[index - 1] as TableBlock
                                // Shift+↑:进锚点选中态(整表外框高亮),用户可继续按 Backspace/Enter 操作整表
                                // 默认 ↑:直接进入上方表格的末行 cell(末尾光标)
                                return@onPreviewKeyEvent if (event.isShiftPressed) {
                                    blockState.navSelectTable(prevTable.id, block.id)
                                    true
                                } else {
                                    blockState.requestEnterTableFromAbove(prevTable.id)
                                }
                            }
                        }
                    }
                    if (event.key == Key.DirectionDown) {
                        if (selection.collapsed && selection.min >= textLen) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index >= 0 && index < blockState.blocks.size - 1 && blockState.blocks[index + 1] is TableBlock) {
                                val nextTable = blockState.blocks[index + 1] as TableBlock
                                return@onPreviewKeyEvent if (event.isShiftPressed) {
                                    blockState.navSelectTable(nextTable.id, block.id)
                                    true
                                } else {
                                    blockState.requestEnterTableFromBelow(nextTable.id)
                                }
                            }
                        }
                    }
                    // 普通 ←(无 Shift)在块首位置(pos == 0)+ 上方为 TableBlock:
                    // 跳到上方表格末行末格。
                    if (event.key == Key.DirectionLeft && !event.isShiftPressed) {
                        if (selection.collapsed && selection.min == 0) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index > 0 && blockState.blocks[index - 1] is TableBlock) {
                                val prevTable = blockState.blocks[index - 1] as TableBlock
                                val lastRow = prevTable.rows.lastOrNull()
                                if (lastRow != null) {
                                    val lastCell = lastRow.cells.lastOrNull()
                                    if (lastCell != null) {
                                        blockState.pendingEnterTableCell = PendingEnterTableCell(
                                            tableId = prevTable.id, rowId = lastRow.rowId, cellId = lastCell.cellId, caretAtEnd = true
                                        )
                                        return@onPreviewKeyEvent true
                                    }
                                }
                            }
                        }
                    }
                    // 普通 →(无 Shift)在块末位置(pos >= textLen)+ 下方为 TableBlock:
                    // 跳到下方表格首行首格。
                    if (event.key == Key.DirectionRight && !event.isShiftPressed) {
                        if (selection.collapsed && selection.min >= textLen) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index >= 0 && index < blockState.blocks.size - 1 && blockState.blocks[index + 1] is TableBlock) {
                                val nextTable = blockState.blocks[index + 1] as TableBlock
                                val firstRow = nextTable.rows.firstOrNull()
                                if (firstRow != null) {
                                    val firstCell = firstRow.cells.firstOrNull()
                                    if (firstCell != null) {
                                        blockState.pendingEnterTableCell = PendingEnterTableCell(
                                            tableId = nextTable.id, rowId = firstRow.rowId, cellId = firstCell.cellId, caretAtEnd = false
                                        )
                                        return@onPreviewKeyEvent true
                                    }
                                }
                            }
                        }
                    }
                    // Shift+← 在 **块首位置**(光标 pos == 0)+ 上方为 TableBlock:
                    // 进入该表的 **右高亮**(AnchorPosition.After)。语义是"光标想往左退到上表
                    // 右边界",视觉上把高亮锚在上表右侧,与 Shift+↑ 的左高亮形成左右对称。
                    // 普通 ←(无 Shift)不在此处理,沿用 BasicTextField/cursor 默认行为。
                    if (event.key == Key.DirectionLeft && event.isShiftPressed) {
                        if (selection.collapsed && selection.min == 0) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index > 0 && blockState.blocks[index - 1] is TableBlock) {
                                val prevTable = blockState.blocks[index - 1] as TableBlock
                                blockState.navSelectTableAfter(prevTable.id, block.id)
                                return@onPreviewKeyEvent true
                            }
                        }
                    }
                    // Shift+→ 在 **块末位置**(光标 pos == textLen)+ 下方为 TableBlock:
                    // 进入该表的 **左高亮**(AnchorPosition.Before)。语义是"光标想往右
                    // 推过下表左边界",落在下表左侧。
                    if (event.key == Key.DirectionRight && event.isShiftPressed) {
                        if (selection.collapsed && selection.min >= textLen) {
                            val index = blockState.findBlockIndex(block.id)
                            if (index >= 0 && index < blockState.blocks.size - 1 && blockState.blocks[index + 1] is TableBlock) {
                                val nextTable = blockState.blocks[index + 1] as TableBlock
                                blockState.navSelectTable(nextTable.id, block.id)
                                return@onPreviewKeyEvent true
                            }
                        }
                    }
                }
                false
            }
            .onFocusChanged { focusState ->
                if (focusState.isFocused) {
                    // 标记"用户曾主动 focus 过任意 RichTextTextField",用于:
                    // - emoji 插入后:仅当此标志为 true 才 requestFocus()(避免无焦点时弹软键盘)
                    // - hyperlink / 表格 / 导出 dialog 关闭后:仅当此标志为 true 才恢复焦点
                    blockState.hasUserFocusedAnyField = true
                    val keepNavSelection = (blockState.navSelectedTableId != null || blockState.navRowAnchorTableId != null) && blockState.focusedBlockId == block.id
                    blockState.focusBlock(block.id)
                    blockState.clearTableSelection()
                    if (!keepNavSelection) {
                        blockState.navClearSelection()
                    }
                }
            },
        placeholder = "",
        focusRequester = focusRequester,
        onUserValueChange = { oldValue, newValue ->
            val consumed = blockState.tryHandleTextBlockValueChangeBeforeApply(block.id, oldValue, newValue)
            if (!consumed && blockState.editorCursor is EditorCursor.BlockAnchor) {
                blockState.navClearSelection()
            }
            consumed
        },
            onUndo = { blockState.undo() },
            onRedo = { blockState.redo() }
            )
        }
    }

    LaunchedEffect(blockRichTextState.textFieldValue) {
        if (blockRichTextState.textFieldValue.composition != null) return@LaunchedEffect
        blockState.updateBlockContent(block.id)
        if (blockState.editorCursor !is EditorCursor.BlockAnchor) {
            blockState.navClearSelection()
        }
    }
    // 注意:不再在 TextBlockItem 内部监听 `focusedBlockId` 并自动 requestFocus。
    // 旧实现在 LazyColumn 滚动让该 block 首次进入组合时,只要 focusedBlockId == block.id
    // 就会强制 requestFocus(),抢走表格单元格的焦点并导致移动端自动弹出软键盘。
    // 焦点恢复统一由 BlockRichTextEditor 顶层的 LaunchedEffect(focusRequestVersion) 协调,
    // 该 effect 只在用户显式触发的操作(键盘导航、undo/redo 等)自增 version 时才会 requestFocus。
}
